import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

/**
 * BulkAreaTypeDrawer — set the area type on several areas at once.
 *
 * Single-value field, so this is a radio list rather than checkboxes: exactly
 * one type is applied to every selected area. Uses the same `areaTypes` list the
 * Edit Area drawer renders.
 *
 * Per-type counts ("2 of 3 selected") show the existing spread, and areas that
 * already have the chosen type are dropped from the payload so no pointless
 * writes are sent.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (changes: Array<{area_id, areaTypeId, oldAreaTypeId}>) => Promise<void>,
 *   areaTypes: Array<{ area_type_id: number, area_type_description: string }>,
 *   areas: Array<object>,   // the SELECTED area rows
 *   saving: boolean,
 * }} props
 */
const BulkAreaTypeDrawer = ({ isOpen, onClose, onSave, areaTypes, areas, saving }) => {
  const drawerRef = useRef(null);
  const [selectedTypeId, setSelectedTypeId] = useState('');

  useEffect(() => {
    if (isOpen) setSelectedTypeId('');
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const selected = areas || [];
  const sortedTypes = useMemo(
    () => [...(areaTypes || [])].sort((a, b) =>
      (a.area_type_description || '').localeCompare(b.area_type_description || '', undefined, { sensitivity: 'base' })),
    [areaTypes],
  );

  /** How many of the selected areas already have each type */
  const typeCounts = useMemo(() => {
    const counts = {};
    selected.forEach((a) => {
      if (a.area_type_id != null) {
        counts[a.area_type_id] = (counts[a.area_type_id] || 0) + 1;
      }
    });
    return counts;
  }, [selected]);

  /** Only areas whose type actually differs from the chosen one. */
  const changes = useMemo(() => {
    if (!selectedTypeId) return [];
    const target = parseInt(selectedTypeId, 10);
    return selected
      .filter((a) => parseInt(a.area_type_id, 10) !== target)
      .map((a) => ({
        area_id: a.area_id,
        areaTypeId: target,
        oldAreaTypeId: a.area_type_id,
      }));
  }, [selected, selectedTypeId]);

  const hasWork = changes.length > 0;

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!hasWork) return;
    onSave(changes);
  }, [hasWork, changes, onSave]);

  const summary = (() => {
    if (!selectedTypeId) return 'Choose the area type to apply to the selected areas.';
    if (changes.length === 0) return 'No changes — the selected areas already use this type.';
    const n = changes.length;
    const typeName = sortedTypes.find((t) => String(t.area_type_id) === String(selectedTypeId))?.area_type_description || 'this type';
    return `Sets ${n} area${n !== 1 ? 's' : ''} to "${typeName}".`;
  })();

  return (
    <>
      {isOpen && <div className="ap-drawer-backdrop" onClick={onClose} aria-hidden="true" />}

      <div
        ref={drawerRef}
        className={`ap-drawer ap-drawer--md ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Edit area type for selected areas"
      >
        <div className="ap-drawer__header">
          <h2 className="ap-drawer__title">Edit Area Type</h2>
          <button type="button" onClick={onClose} className="ap-drawer__close" aria-label="Close drawer">
            <svg className="ap-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ap-form">
          <div className="ap-form__body">
            <p className="ap-field__label" style={{ marginBottom: 0 }}>
              {selected.length} area{selected.length !== 1 ? 's' : ''} selected
            </p>

            <div>
              <p className="ap-field__label">
                Area Type <span className="ap-field__required">*</span>
              </p>
              {sortedTypes.length === 0 ? (
                <p className="ap-cell-text--muted">No area types are available.</p>
              ) : (
                <div className="ap-group-grid">
                  {sortedTypes.map((type) => {
                    const isChecked = String(selectedTypeId) === String(type.area_type_id);
                    const count = typeCounts[type.area_type_id] || 0;
                    return (
                      <label
                        key={type.area_type_id}
                        className={`ap-group-option ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="bulk-area-type"
                          className="ap-checkbox"
                          checked={isChecked}
                          onChange={() => setSelectedTypeId(String(type.area_type_id))}
                        />
                        <span className="ap-group-option__text">
                          {type.area_type_description}
                          <span style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            {count === 0
                              ? 'none of the selected'
                              : `${count} of ${selected.length} selected already`}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <p className="ap-bulk-summary">{summary}</p>
          </div>

          <div className="ap-form__footer">
            <button type="button" onClick={onClose} className="ap-btn ap-btn--cancel">
              Cancel
            </button>
            <button type="submit" disabled={!hasWork || saving} className="ap-btn ap-btn--primary">
              {saving ? 'Saving...' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default BulkAreaTypeDrawer;
